import React, { useState, useRef, useEffect } from 'react'
import mobiscroll from '@mobiscroll/react'
import * as m5 from '@mobiscroll/react5'
import { db } from '../index'
import Nav from './Nav'
import { toDateString, toDate, getTimestamp } from '../Helper'
import dealersOptions from '../dataset/dealers.json'
import stocksOptions from '../dataset/stocks.json'
import { ListNote } from './Config'
import BigNumber from "bignumber.js";
import moment from 'moment'
import FeeEditor from './FeeEditor'
import Search from './Search'

const LONG_RATIO = 0.6 //上市融資成數
const LONG_RATIO_OTC = 0.5 //上櫃融資成數
const SHORT_RATIO = 0.9 //保證金成數

const fee = 0.001425 //標準手續費
const minFeeDef = 20

export const getTax = (t) => {
    //行政規定稅費固定為 無條件捨去
    if (t.action === 'sell') {
        let taxRatio = 0.003
        if (!t.credit) { //信用交易固定為0.003
            if (t.type === 'etf' || t.id.startsWith('00')) { //etf固定為0.001
                taxRatio = 0.001
            } else if (t.type === 'day-trade') {
                taxRatio = 0.0015
            }
        }
        return new BigNumber(t.amount).times(new BigNumber(taxRatio))
            .integerValue(BigNumber.ROUND_FLOOR).toNumber()

    } else {
        return 0
    }
}

const calcInterest = (amount, rate, sdate, edate) => {
    const days = moment(edate).diff(moment(sdate), 'd')
    return Math.floor(new BigNumber(amount).times(new BigNumber(rate / 100)).times(days).div(365).toNumber())
}

export const getInterest = (creditData, curr) => {
    let interest = 0
    const withInterest = updateInterest(creditData, curr).filter(m => m._id === curr._id)
    if (withInterest.length > 0) {
        interest = withInterest[0].interest
    }
    return interest
}

export const updateInterest = (creditData, curr, isDelete) => {
    let needUpdate = []
    let initData = creditData.filter(d => d.id === curr.id && d._id !== curr._id && d.type === 'normal'
        && d.dealer.id === curr.dealer.id).reverse() //去掉正在編輯的交易及當沖交易比依日期由先至後
    if (!isDelete) {
        let idx = initData.findIndex(d => d.date > curr.date)
        idx = idx > -1 ? idx : initData.length
        initData.splice(idx, 0, curr) //加回正在編輯的交易
    }

    let data = initData.map(d => ({ ...d, out: 0 })) //避免修改原始陣列且沖銷數量全部重置

    const longArr = data.filter(t => t.creditType === 'long' && t.action === 'buy')
    const shortArr = data.filter(t => t.creditType === 'short' && t.action === 'sell')

    //資買資賣沖銷 先進先出
    for (const sell of data.filter(d => d.creditType === 'long' && d.action === 'sell')) {
        let interest = 0
        let rest = sell.quantity
        for (let buy of longArr) {
            if (moment(buy.date).isBefore(moment(sell.date))) {
                const stock = buy.quantity - buy.out
                if (stock >= rest) {
                    interest += calcInterest(buy.loan * rest / buy.quantity, sell.dealer.mtr, buy.date, sell.date)
                    buy.out += rest
                    rest = 0
                } else if (stock > 0) {
                    interest += calcInterest(buy.loan * stock / buy.quantity, sell.dealer.mtr, buy.date, sell.date)
                    buy.out += stock
                    rest -= stock
                }
                if (rest <= 0)
                    break;
            }
        }
        sell.interest = rest === 0 ? interest : 0
        if (curr._id === sell._id || creditData.filter(c => c._id === sell._id)[0].interest !== sell.interest) {
            needUpdate.push({ _id: sell._id, interest: sell.interest })
        }
    }
    //券賣券買沖銷 先進先出
    for (const buy of data.filter(d => d.creditType === 'short' && d.action === 'buy')) {
        let interest = 0
        let rest = buy.quantity
        for (let sell of shortArr) {
            if (moment(sell.date).isBefore(moment(buy.date))) {
                const stock = sell.quantity - sell.out
                const base = (sell.amount - (sell.cfee ? sell.cfee : sell.fee) - getTax(sell))
                    + (sell.amount - sell.loan)
                if (stock >= rest) {
                    interest += calcInterest(base * (rest / sell.quantity), buy.dealer.ssr, sell.date, buy.date)
                    sell.out += rest
                    rest = 0
                } else if (stock > 0) {
                    interest += calcInterest(base * (stock / sell.quantity), buy.dealer.ssr, sell.date, buy.date)
                    sell.out += stock
                    rest -= stock
                }
                if (rest <= 0)
                    break;
            }
        }
        buy.interest = rest === 0 ? -interest : 0
        if (curr._id === buy._id || creditData.filter(c => c._id === buy._id)[0].interest !== buy.interest) {
            needUpdate.push({ _id: buy._id, interest: buy.interest })
        }
    }

    return needUpdate //回傳利息更新資訊
}

export const getBorrowStockFee = (transData, bsf) => {
    const { credit, creditType, action, amount } = transData
    if (credit && (creditType === 'short' && action === 'sell') && amount > 0)
        return Math.floor(amount * bsf / 100)
    else
        return 0
}

//2021.06.28 手續費改採無條件捨去 //2024.08.16 調整計算方式
export const getTradeFee = (t, discount, minFeeR, minFee) => {
    if (t.action === 'profit') {
        return 0
    } else {
        let mfr = minFeeR || minFeeDef
        const minf = t.quantity < 1000 ? mfr : minFee || minFeeDef
        //const finalFee = Math.floor(t.amount * fee) - Math.floor(t.amount * fee * (1 - discount))
        const finalFee = Math.floor(t.amount * fee * discount)
        const tradeFee = Math.max(finalFee, minf)
        return Math.floor(tradeFee)

    }
}


const FeeHint = ({ transData }) => {
    const tradeFee = getTradeFee(transData, transData.dealer.dc, transData.dealer.mfr, transData.dealer.mf)
    let tax = 0
    if (transData.action === 'sell') {
        tax = getTax(transData)
    }
    return <>
        手續費 {tradeFee} {tax > 0 && <>稅費 {tax}</>}
    </>
}

const CreditHint = ({ type, creditType, action, loan, interest, amount, sfee }) => {
    return (
        <div>
            {type === 'normal' &&
                <>
                    {
                        (creditType === 'long' && action === 'buy') && '融資金額 ' + loan
                    }
                    {
                        (creditType === 'short' && action === 'sell') && '保證金 ' + (isNaN(amount - loan) ? 0
                            : (amount - loan))
                    }
                    {
                        creditType === 'short' && action === 'buy' ?
                            ' 利息收入 ' + (interest < 0 ? -(interest) : '無法沖銷或不足1元') :
                            creditType === 'long' && action === 'sell' ?
                                ' 利息支出 ' + (interest > 0 ? interest : '無法沖銷或不足1元') : ''
                    }
                </>
            }
            {
                (creditType === 'short' && action === 'sell') && ' 借券費 ' + sfee
            }
        </div >
    )
}

const Transaction = ({ theme, user, defDealer, transaction, dealers, afterSave, onClose, creditData,
    searching, setSearching, searchContext, setSearchContext }) => {

    const emptyTransaction = {
        user,
        date: toDateString(new Date()),
        dealer: defDealer ? dealers.filter(d => d.id === defDealer)[0] : dealers[0],
        action: 'buy',
        profitType: 'profit',
        id: '',
        name: '',
        mkt: '',
        type: 'normal',
        quantity: 1000,
        price: '',
        amount: 0,
        fee: 0,
        tax: 0,
        cfee: '',
        memo: '',
        credit: false,
        creditType: 'long',
        loan: 0,
        interest: 0,
        sfee: 0
    }

    if (transaction) {
        transaction = { ...emptyTransaction, ...transaction }
    }

    const initData = transaction || emptyTransaction

    const idEl = useRef(null)
    const quantityProfitEl = useRef(null)
    const quantityEl = useRef(null)
    const priceEl = useRef(null)
    const [qType, setQType] = useState(initData.quantity % 1000 !== 0 ? '0' : '1')
    const [transData, setTransData] = useState(initData)
    const [idError, setIdError] = useState('')
    const [priceError, setPriceError] = useState('')
    const [quantityErrorProfit, setQuantityErrorProfit] = useState('')
    const [quantityError, setQuantityError] = useState('')
    const [editFee, setEditFee] = useState(null)
    const [creditSwitch, setCreditSwitch] = useState(false)
    const [otherTranscationsInterest, setOtherTranscationsInterest] = useState([])

    useEffect(() => {
        if (transData.dealer) {
            const { mtr, ssr, bsf } = transData.dealer
            setCreditSwitch(mtr && ssr && bsf)
        }

    }, [transData, creditData])

    useEffect(() => {
        if (searchContext.length > 0) {
            const id = searchContext.split(' ')[0]
            const search = stocksOptions.filter(s => s.id === id)
            let name = ''
            let mkt = ''
            if (search.length > 0) {
                name = search[0].name
                mkt = search[0].type === '上市' ? 'tse' : search[0].type === '上櫃' ? 'otc' : 'tpex'
            }
            setTransData({ ...transData, id, name: name, mkt: mkt })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchContext])

    const openFeeEditor = () => {
        if (transData.dealer.mfr === undefined) {
            transData.dealer.mfr = 20
        }
        setEditFee(transData)
    }

    const updateFee = (dealer, cfee) => {
        let newData = { ...transData, dealer }
        reCalcThanSetTransData(creditData, newData, cfee)
        setEditFee(null)
    }

    const reCalc = (creditData, newData, cfee) => {
        if (newData.action !== 'profit' && newData.price !== '') {
            newData.amount = new BigNumber(newData.price).times(new BigNumber(newData.quantity))
                .integerValue(BigNumber.ROUND_FLOOR).toNumber() //交易金額採無條件捨去 2024.07.17

            //使用者自訂費用
            if (typeof cfee !== 'undefined')
                newData.cfee = cfee
            newData.fee = getTradeFee(newData, newData.dealer.dc, newData.dealer.mfr, newData.dealer.mf)
            const tax = getTax(newData)
            newData.tax = tax
            newData.fee += tax //稅費列入費用

            const { mkt, creditType, type, action, amount } = newData
            const ratio = creditType === 'long' ? mkt === 'otc' ? LONG_RATIO_OTC : LONG_RATIO : SHORT_RATIO
            if (type === 'normal' && amount > 0) {
                if (creditType === 'long' && action === 'buy') {
                    newData.loan = Math.floor(amount * ratio / 1000) * 1000
                } else if (creditType === 'short' && action === 'sell') {
                    newData.loan = amount - (Math.ceil(amount * ratio / 100) * 100)
                }
            }
            const sfee = getBorrowStockFee(newData, newData.dealer.bsf)
            newData.sfee = sfee
            newData.fee += sfee //借券費列入費用
            const update = updateInterest(creditData, newData)
            if (update.length > 0) {
                let interest = 0
                const withInterest = update.filter(m => m._id === newData._id)
                if (withInterest.length > 0) {
                    interest = withInterest[0].interest
                }
                newData.interest = interest
                newData.fee += interest
            }
            setOtherTranscationsInterest(update.filter(d => d._id !== newData._id))
        }
        return newData
    }

    const reCalcThanSetTransData = (data, newData, fee) => {
        setTransData(reCalc(data, newData, fee))
    }

    const change = (ev) => {
        const value = ev.target.value
        const property = ev.target.name
        switch (property) {
            case 'id':
                let name = ''
                let mkt = ''
                if (value.length > 3) {
                    const search = stocksOptions.filter(s => s.id === value)
                    if (search.length > 0) {
                        name = search[0].name
                        mkt = search[0].type === '上市' ? 'tse' : search[0].type === '上櫃' ? 'otc' : 'tpex'
                    }
                }
                setTransData({ ...transData, id: value, name: name, mkt: mkt })
                break
            case 'amount': //配息
                setTransData({ ...transData, amount: value === '' ? '' : parseFloat(value) })
                break
            case 'profitType':
                let quantity = transData.quantity
                if (value === 'lending') {
                    quantity = 0
                }
                setTransData({ ...transData, profitType: value, quantity })
                break
            default:
                let newData = { ...transData }
                if (property === 'price') {
                    newData[property] = value === '' ? '' : parseFloat(value)
                    //newData = reCalc(creditData, newData)
                } else if (property === 'action') {
                    if (value === 'profit') {
                        newData.price = ''
                        newData.quantity = ''
                        newData.amount = ''
                    } else {
                        if (transData.action === 'profit') { //其它轉換為買賣
                            newData.quantity = 1000
                            newData.price = ''
                            newData.amount = ''
                        }
                    }
                    newData[property] = value
                } else if (property === 'creditType') {
                    if (value === 'short') {
                        newData.action = 'sell'
                    } else {
                        newData.action = 'buy'
                    }
                    newData[property] = value
                } else {
                    newData[property] = value
                }
                reCalcThanSetTransData(creditData, newData)
                break
        }
    }

    const changeDealer = (ev, inst) => {
        const value = inst.getVal()
        reCalcThanSetTransData(creditData, { ...transData, dealer: dealers.filter(d => d.id === value)[0] })
    }

    const changeDate = (ev) => {
        reCalcThanSetTransData(creditData, { ...transData, date: toDateString(new Date(ev.value)) })
    }

    const changeQ = (ev) => {
        const valueText = ev.target.value
        let v = valueText === '' || valueText === '-' || valueText === 'NaN' ? '' :
            qType === '1' && transData.action !== 'profit' ?
                parseInt(valueText) * 1000 : parseInt(valueText)
        if (v !== '' && transData.action === 'profit') {
            if (transData.profitType === 'reduction') {
                if (v > 0) {
                    v = -v
                }
            }
        }
        let newData = { ...transData, quantity: v }
        if (transData.action !== 'profit')
            newData = reCalc(creditData, newData)
        setTransData(newData)
    }

    const changeQType = (ev) => {
        document.getElementById('q').value = 1
        setQType(ev.target.value)
        let newData = { ...transData }
        if (ev.target.value === '1') {
            newData.quantity = 1000
        } else {
            newData.quantity = 1
        }
        newData = reCalc(creditData, newData)
        setTransData(newData)
    }

    const offlineSkip = () => {
        if (!navigator.onLine) { //離線模式
            setTimeout(() => {
                if (!navigator.onLine) {
                    afterSave(user, 'cache')
                    window.history.back()
                }
            }, 666)
        }
    }

    const save = () => {
        let isOkay = true

        if (action === 'profit') {
            if (transData.amount === '') { //配息空值
                transData.amount = 0
            }
        }

        if (!transData.name) {
            idEl.current.nativeElement.focus()
            setIdError('請輸入股票代號')
            isOkay = false
        } else {
            setIdError('')
        }

        if (action !== 'profit' && !transData.quantity
            && (parseInt(transData.quantity) === 0 || isNaN(transData.quantity))) {
            quantityEl.current.nativeElement.focus()
            setQuantityError('請輸入正確股數')
            isOkay = false
        } else {
            setQuantityError('')
        }

        if (action === 'profit' && !transData.quantity && isNaN(transData.quantity)) { //股利允許股數為0
            quantityProfitEl.current.nativeElement.focus()
            setQuantityErrorProfit('請輸入正確股數')
            isOkay = false
        } else {
            setQuantityErrorProfit('')
        }

        if (action !== 'profit' && !transData.price
            && (parseFloat(transData.price) === 0 || isNaN(transData.price) || transData.price === '')) {
            priceEl.current.nativeElement.focus()
            setPriceError('請輸入正確價格')
            isOkay = false
        } else {
            setPriceError('')
        }

        if (transData.quantity === '') {
            transData.quantity = 0
        }

        if (isOkay) {
            if (otherTranscationsInterest.length > 0) {
                otherTranscationsInterest.forEach(({ _id, interest }) => {
                    db.collection('transactions').doc(_id).set({ interest, ut: getTimestamp() }, { merge: true })
                        .then(() => {
                            afterSave(user, 'cache')
                        }).catch((error) => {
                            console.error("Error writing document: ", error)
                        })
                })
            }
            if (transData._id) {
                let nData = { ...transData, ut: getTimestamp() }
                delete nData._id
                db.collection('transactions').doc(transData._id).set(nData)
                    .then(() => {
                        afterSave(user, 'cache')
                        window.history.back()
                    }).catch((error) => {
                        console.error("Error writing document: ", error)
                    })
                offlineSkip()
            } else {
                db.collection('transactions').add({ ...transData, ut: getTimestamp() })
                    .then(() => {
                        afterSave(user, 'cache')
                        window.history.back()
                    }).catch((error) => {
                        console.error("Error writing document: ", error)
                    })
                offlineSkip()
            }
        }

    }

    const del = () => {
        if (transData.credit) {
            const otherTranscationsInterest = updateInterest(creditData, transData, true)
            if (otherTranscationsInterest.length > 0) {
                otherTranscationsInterest.forEach(({ _id, interest }) => {
                    db.collection('transactions').doc(_id).set({ interest, ut: getTimestamp() }, { merge: true })
                        .then(() => {
                            afterSave(user, 'cache')
                        }).catch((error) => {
                            console.error("Error writing document: ", error)
                        })
                })
            }
        }
        //2025.06.05 改成不刪除 增加一個 delete flag
        db.collection('transactions').doc(transData._id).set({ delete: true, ut: getTimestamp() }, { merge: true })
            .then(() => {
                afterSave(user, 'cache')
                window.history.back()
            }).catch((error) => {
                console.error("Error removing document: ", error);
            })
        offlineSkip()
    }

    const segmentColor = () => ({
        buy: 'danger',
        buyShort: 'danger',
        sell: 'success',
        sellShort: 'success',
        profit: 'primary'
    }[transData.action])

    const unarchive = () => {
        db.collection('transactions').doc(transData._id)
            .update({
                archive: 0, ut: getTimestamp()
            }).then(() => {
                afterSave(user, 'cache')
                window.history.back()
            }).catch((error) => {
                console.error("Error writing document: ", error)
            })
    }

    const plusQ = () => {
        const step = qType === '1' && transData.action !== 'profit' ?
            1000 : 1
        if (transData.quantity === '') {
            transData.quantity = 0
        }
        let newData = { ...transData, quantity: transData.quantity + step }
        if (transData.action !== 'profit')
            newData = reCalc(creditData, newData)
        setTransData(newData)

    }

    const minusQ = () => {
        const step = qType === '1' && transData.action !== 'profit' ?
            1000 : 1
        if (transData.quantity - step > 0) {
            let newData = { ...transData, quantity: transData.quantity - step }
            if (transData.action !== 'profit')
                newData = reCalc(creditData, newData)
            setTransData(newData)
        }
    }

    const {
        date,
        dealer,
        action,
        profitType,
        id,
        name,
        type,
        quantity,
        price,
        amount,
        fee,
        cfee,
        archive,
        memo,
        credit,
        creditType
    } = transData

    const txt = theme === 'dark' ? 'text-white' : 'text-black'
    const readOnly = archive === 1
    const color = readOnly ? undefined : segmentColor()
    const btnCls = theme === 'dark' ? 'btn-dark bg-dark' : 'btn-light bg-white'

    const quantityTxt = qType === '1' && transData.action !== 'profit' && quantity !== '' ? Math.round(quantity / 1000) : quantity

    const handleCredit = (ev) => {
        let newTransData = { ...transData }
        const isCredit = ev.target.value === 'credit'
        newTransData.credit = isCredit
        reCalcThanSetTransData(creditData, newTransData)
    }

    const openSearch = () => {
        window.location.hash = 'search'
        setSearching(true)
    }

    const closeSearch = () => {
        window.history.back()
        setSearching(false)
    }

    const search = (item) => {
        setSearchContext(item)
        closeSearch()
    }

    const dealersOptionsWithCustom = [...dealersOptions.filter(d => dealers.map(d => d.id).indexOf(d.id) > -1)
        .map(o => ({ value: o.id, text: o.name })), ...dealers.filter(d => d.id.startsWith('sb')).map(o => ({ value: o.id, text: o.name }))]

    const profitLabels = {
        profit: { amount: '股息金額', quantity: '配股股數', hint: '請輸入實收的股數及金額' },
        reduction: { amount: '退還金額', quantity: '減資股數', hint: '減資會扣除持股並將退款列收入' },
        lending: { amount: '借券收益', hint: '請輸入實收的金額' }
    }

    return (
        <>{
            searching ? <Search theme={theme} setItem={search} onClose={closeSearch} /> :
                <>
                    <Nav theme={theme} title={transData._id ? '編輯交易' : '新增交易'} onBack={onClose} />
                    <div className={'App-form ' + txt}>
                        <m5.Select
                            inputStyle='underline'
                            label='證券戶'
                            disabled={readOnly}
                            display="bottom"
                            value={dealer.id}
                            setText="確定"
                            cancelText="取消"
                            onChange={changeDealer}
                            data={dealersOptionsWithCustom}>
                        </m5.Select>
                        <m5.Datepicker
                            inputStyle='underline'
                            disabled={readOnly}
                            label="日期"
                            touchUi={true}
                            locale={m5.localeZh}
                            setText="確定"
                            controls={['calendar']}
                            value={toDate(date)} onChange={changeDate}
                        />
                        {creditSwitch &&
                            <div className="pr-4">
                                <m5.SegmentedGroup name="credit" disabled={readOnly} onChange={handleCredit}>
                                    <m5.Segmented value="normal" defaultChecked={!credit}>一般交易</m5.Segmented>
                                    <m5.Segmented value="credit" defaultChecked={credit}>信用交易</m5.Segmented>
                                </m5.SegmentedGroup>
                            </div>
                        }
                        <m5.Page className='sb-page-no-safe-top'>
                            <div className="mbsc-row px-3 w-100">
                                <div className="mbsc-col-7 p-0">
                                    <m5.Input
                                        className='mt-3 mb-2'
                                        disabled={readOnly}
                                        inputStyle="box" type="text" ref={idEl} name="id" value={id}
                                        onChange={change} placeholder="股票代號"
                                        error={idError}
                                    />
                                </div>
                                <div className="mbsc-col-4 p-0 mt-2">
                                    <div className="d-flex align-items-center h-100">{name}</div>
                                </div>
                                {
                                    !readOnly &&
                                    <div className="mbsc-col-1 p-0">
                                        <div className='mt-3'>
                                            <span className="mbsc-ic mbsc-ic-material-search m-2"
                                                style={{ fontSize: '2rem' }}
                                                onClick={openSearch}></span>
                                        </div>
                                    </div>
                                }
                            </div>
                            <div className="mbsc-form-group-title border-bottom-0 d-flex justify-content-between">
                                <span>{credit ? '信用交易' : '一般交易'}</span>
                            </div>
                            {
                                credit ?
                                    <div>
                                        <div className="px-3 bg-transparent">
                                            <mobiscroll.Segmented name="type" disabled={readOnly} onChange={change} value="normal" checked={"normal" === type}>一般</mobiscroll.Segmented>
                                            <mobiscroll.Segmented name="type" disabled={readOnly} onChange={change} value="day-trade" checked={"day-trade" === type}>當沖</mobiscroll.Segmented>
                                        </div>
                                        <div className="px-3 bg-transparent">
                                            <mobiscroll.Segmented disabled={readOnly} name="creditType" onChange={change} value="long" checked={"long" === creditType}>融資</mobiscroll.Segmented>
                                            <mobiscroll.Segmented disabled={readOnly} name="creditType" onChange={change} value="short" checked={"short" === creditType}>融券</mobiscroll.Segmented>
                                        </div>
                                        <div className="px-3 bg-transparent">
                                            <mobiscroll.Segmented disabled={readOnly} name="action" color={color} onChange={change} value="buy" checked={"buy" === action}>買</mobiscroll.Segmented>
                                            <mobiscroll.Segmented disabled={readOnly} name="action" color={color} onChange={change} value="sell" checked={"sell" === action}>賣</mobiscroll.Segmented>
                                        </div>
                                    </div> : //一般交易
                                    <>
                                        <div className="px-3 bg-transparent">
                                            <mobiscroll.Segmented disabled={readOnly} name="action" color={color} onChange={change} value="buy" checked={"buy" === action}>買</mobiscroll.Segmented>
                                            <mobiscroll.Segmented disabled={readOnly} name="action" color={color} onChange={change} value="sell" checked={"sell" === action}>賣</mobiscroll.Segmented>
                                            <mobiscroll.Segmented disabled={readOnly} name="action" color={color} onChange={change} value="profit" checked={"profit" === action}>其它</mobiscroll.Segmented>
                                        </div>
                                        {
                                            action === 'profit' ?
                                                <div className="px-3 bg-transparent">
                                                    <mobiscroll.Segmented name="profitType" disabled={readOnly} onChange={change} value="profit" checked={"profit" === profitType}>股利</mobiscroll.Segmented>
                                                    <mobiscroll.Segmented name="profitType" disabled={readOnly} onChange={change} value="reduction" checked={"reduction" === profitType}>減資</mobiscroll.Segmented>
                                                    <mobiscroll.Segmented name="profitType" disabled={readOnly} onChange={change} value="lending" checked={"lending" === profitType}>借券</mobiscroll.Segmented>
                                                </div> :
                                                <>
                                                    <div className="px-3 bg-transparent">
                                                        <mobiscroll.Segmented name="type" disabled={readOnly} onChange={change} value="normal" checked={"normal" === type}>一般</mobiscroll.Segmented>
                                                        <mobiscroll.Segmented name="type" disabled={readOnly} onChange={change} value="day-trade" checked={"day-trade" === type}>當沖</mobiscroll.Segmented>
                                                    </div>
                                                    <div className="px-3 bg-transparent">
                                                        <mobiscroll.Segmented name="qType" disabled={readOnly} onChange={changeQType} value="1" checked={"1" === qType}>整股</mobiscroll.Segmented>
                                                        <mobiscroll.Segmented name="qType" disabled={readOnly} onChange={changeQType} value="0" checked={"0" === qType}>零股</mobiscroll.Segmented>
                                                    </div>
                                                </>
                                        }
                                    </>
                            }
                            {action === 'profit' ?
                                <>
                                    {profitType !== 'lending' && <m5.Input
                                        label={profitLabels[profitType].quantity}
                                        disabled={readOnly}
                                        error={quantityErrorProfit}
                                        ref={quantityProfitEl}
                                        id="q"
                                        inputStyle="box"
                                        type="number"
                                        defaultValue={quantity}
                                        value={quantity}
                                        onChange={changeQ} />}
                                    <m5.Input label={profitLabels[profitType].amount}
                                        disabled={readOnly}
                                        inputStyle="box"
                                        type="number"
                                        name="amount"
                                        defaultValue={amount}
                                        onChange={change}
                                    />
                                    <ListNote style={{ marginTop: '-0.75rem' }} text={profitLabels[profitType].hint} />
                                </> :
                                <>
                                    <div className="d-flex justify-content-start">
                                        <m5.Input
                                            label={qType === '1' ? '張數' : '股數'}
                                            disabled={readOnly}
                                            error={quantityError}
                                            ref={quantityEl}
                                            id="q"
                                            inputStyle="box"
                                            type="number"
                                            value={quantityTxt}
                                            onChange={changeQ} />
                                        <div className="d-flex justify-content-end my-3 mr-3">
                                            <button type="button" className={"mr-1 py-2 border-0 rounded-lg btn " + btnCls} onClick={plusQ} disabled={readOnly}>
                                                <i className="bi bi-plus-lg"></i>
                                            </button>
                                            <button type="button" className={"ml-1 py-2 border-0 rounded-lg btn " + btnCls} onClick={minusQ} disabled={quantityTxt === 1 || readOnly}>
                                                <i className="bi bi-dash-lg"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-start">
                                        <m5.Input
                                            className='mt-0'
                                            disabled={readOnly}
                                            label="價格"
                                            error={priceError}
                                            ref={priceEl}
                                            inputStyle="box"
                                            type="number"
                                            name="price"
                                            onChange={change}
                                            defaultValue={price} />
                                        <div style={{ width: '7.25rem', paddingLeft: 2 }}></div>
                                    </div>
                                    <div className={'mt-0 mx-3 text-center alert alert-' + segmentColor()} style={{ borderRadius: '0.5rem' }}>
                                        價金 {isNaN(amount) ? 0 : amount} {cfee !== '' && cfee !== undefined ? <>費用 {cfee}</> : isNaN(fee) ? 0 : <FeeHint transData={transData} />}
                                        {!readOnly && <span className="mbsc-ic mbsc-ic-pencil ml-2" onClick={openFeeEditor}></span>}
                                        {credit && <CreditHint {...transData} />}
                                    </div>
                                </>
                            }
                            <m5.Textarea
                                className="mx-3 mb-1"
                                name="memo"
                                defaultValue={memo}
                                placeholder="至多50字"
                                maxLength="50"
                                onChange={change}
                                inputStyle="box"
                                labelStyle="stacked"
                                label="備註">
                            </m5.Textarea>

                        </m5.Page>
                        {readOnly ?
                            <div className="d-flex justify-content-end px-3 pb-2">
                                <button type="button" className="btn btn-primary rounded-pill w-50 m-2 py-2"
                                    onClick={unarchive}>
                                    解除封存
                                </button>
                            </div> :
                            <div className="d-flex justify-content-end px-3 pb-2">
                                {(transaction && transaction._id) &&
                                    <button type="button" className="btn btn-danger rounded-pill w-50 m-2 py-2"
                                        onClick={del}>
                                        刪除
                                    </button>}
                                <button type="button" className="btn btn-primary rounded-pill w-50 m-2 py-2"
                                    onClick={save}>
                                    儲存
                                </button>
                            </div>}
                        <FeeEditor
                            dealers={dealers}
                            creditData={creditData}
                            transData={editFee}
                            theme={theme}
                            onCancel={() => {
                                setEditFee(null)
                            }}
                            onSet={updateFee}
                        />
                    </div>
                </>
        }
        </>
    )
}

export default Transaction